import { ReactNode } from 'react';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { createForm, useCreateFormStore } from '@components/form';
import { IconReplace } from '@components/icons/replace';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

type Props = {
  onConfirm(name: string): void;
  name?: string;
  defaultName: string;
  card: ReactNode;
};

export const useSetCustomNameModal = () => useModal(SetCustomNameModal);
export type ShowCustomNameModal = ReturnType<typeof useSetCustomNameModal>;

const Form = createForm<{ columnName: string }>();

const SetCustomNameModal = ({ name, card, defaultName, onConfirm }: Props) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const { name: newName } = formStore.useWatchValues(['columnName']);

  return (
    <Modal>
      <Form
        store={formStore}
        space={0}
        onSubmit={(values) => {
          onConfirm(values.columnName);
          hideModal();
        }}
      >
        <Modal.SimpleHeader title={t('title')} />
        <Modal.Content>
          <Stack space={6}>
            {card}
            <Form.TextField
              hideRequiredIndicator
              autoFocus
              autoComplete="off"
              defaultValue={name || defaultName}
              id="columnName"
              label={t('fieldName')}
              validators={['required']}
            />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <ButtonGroup align="right">
            <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
            <Button
              variant="secondary"
              text={t('reset')}
              iconLeft={<IconReplace />}
              isDisabled={newName === defaultName}
              onClick={() => {
                formStore.actions.setValue('columnName', defaultName);
              }}
            />
            <Form.Submit variant="primary-blue" text={t('core.save')} />
          </ButtonGroup>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Set custom name',
    fieldName: 'Field name',
    reset: 'Reset name',
  },
};
